
import BaseButton from '@/components/BaseButton.vue';
import {
  defineComponent,
  reactive,
  computed,
  onMounted,
  onBeforeUnmount,
  ref,
  watch,
  nextTick,
} from 'vue';

import LazyBackgroundMedia from '@/components/LazyBackgroundMedia.vue';

export default defineComponent({
  components: {
    BaseButton,
    LazyBackgroundMedia,
  },
  props: {
    contentAlign: {
      type: String,
      required: false,
      default: () => 'top',
    },
    height: {
      type: String,
      required: false,
      default: 'medium',
    },
    mediaId: {
      type: String,
      required: false,
      default: () => '0',
    },
    mediaSizes: {
      type: [String, Object],
      required: false,
    },
    mediaAlt: {
      type: String,
      required: false,
      default: () => '',
    },
    mediaCalcOneId: {
      type: String,
      required: false,
      default: () => '0',
    },
    mediaCalcOneSizes: {
      type: [String, Object],
      required: false,
    },
    mediaCalcOneAlt: {
      type: String,
      required: false,
      default: () => '',
    },
    mediaCalcTwoId: {
      type: String,
      required: false,
      default: () => '0',
    },
    mediaCalcTwoSizes: {
      type: [String, Object],
      required: false,
    },
    mediaCalcTwoAlt: {
      type: String,
      required: false,
      default: () => '',
    },
    mediaCalcThreeId: {
      type: String,
      required: false,
      default: () => '0',
    },
    mediaCalcThreeSizes: {
      type: [String, Object],
      required: false,
    },
    mediaCalcThreeAlt: {
      type: String,
      required: false,
      default: () => '',
    },
    mediaCalcFourId: {
      type: String,
      required: false,
      default: () => '0',
    },
    mediaCalcFourSizes: {
      type: [String, Object],
      required: false,
    },
    mediaCalcFourAlt: {
      type: String,
      required: false,
      default: () => '',
    },
    backgroundPositionY: {
      type: String,
      required: false,
      default: () => 'top',
    },
    backgroundPositionX: {
      type: String,
      required: false,
      default: () => 'center',
    },
    backgroundCalcOnePositionY: {
      type: String,
      required: false,
      default: () => 'top',
    },
    backgroundCalcOnePositionX: {
      type: String,
      required: false,
      default: () => 'center',
    },
    backgroundCalcTwoPositionY: {
      type: String,
      required: false,
      default: () => 'top',
    },
    backgroundCalcTwoPositionX: {
      type: String,
      required: false,
      default: () => 'center',
    },
    backgroundCalcThreePositionY: {
      type: String,
      required: false,
      default: () => 'top',
    },
    backgroundCalcThreePositionX: {
      type: String,
      required: false,
      default: () => 'center',
    },
    backgroundCalcFourPositionY: {
      type: String,
      required: false,
      default: () => 'top',
    },
    backgroundCalcFourPositionX: {
      type: String,
      required: false,
      default: () => 'center',
    },
    fontColor: {
      type: String,
      required: false,
      default: () => 'white',
    },
    pageUrl: {
      type: String,
      required: false,
      default: () => null,
    },
    pageUrlLabel: {
      type: String,
      required: false,
      default: () => 'Mehr erfahren',
    },
    calculatorShow: {
      type: String,
      required: false,
      default: () => 'false',
    },
    calculatorBranches: {
      type: String,
      required: false,
      default: () => 'all',
    },
    calculatorLabelExtra: {
      type: String,
      required: false,
      default: () => null,
    },
  },

  setup(props, context) {
    const header = reactive({
      $el: null as null | HTMLElement,
    });
    const heroContainer = ref<InstanceType<typeof HTMLElement>>();
    const root = ref<InstanceType<typeof HTMLElement>>();
    const background = ref<InstanceType<typeof LazyBackgroundMedia>>();
    const heroCalculator = ref<InstanceType<typeof HTMLElement>>();
    const calculatorComponent = ref();
    const isMobile = ref(false);
    const currentSelectedUsage = ref<number | null>(null);
    const currentMedia = computed<Record<symbol, unknown>>(() => {
      const m = {
        id: props.mediaId,
        sizes: props.mediaSizes,
        alt: props.mediaAlt,
        positionX: props.backgroundPositionX,
        positionY: props.backgroundPositionY,
      };

      if (props.calculatorShow === 'true') {
        if (currentSelectedUsage.value === 2 && props.mediaCalcTwoId && props.mediaCalcTwoId !== '0') {
          m.id = props.mediaCalcTwoId;
          m.sizes = props.mediaCalcTwoSizes;
          m.alt = props.mediaCalcTwoAlt;
          m.positionX = props.backgroundCalcTwoPositionX;
          m.positionY = props.backgroundCalcTwoPositionY;
        } else if (currentSelectedUsage.value === 3 && props.mediaCalcThreeId && props.mediaCalcThreeId !== '0') {
          m.id = props.mediaCalcThreeId;
          m.sizes = props.mediaCalcThreeSizes;
          m.alt = props.mediaCalcThreeAlt;
          m.positionX = props.backgroundCalcThreePositionX;
          m.positionY = props.backgroundCalcThreePositionY;
        } else if (currentSelectedUsage.value === 4 && props.mediaCalcFourId && props.mediaCalcFourId !== '0') {
          m.id = props.mediaCalcFourId;
          m.sizes = props.mediaCalcFourSizes;
          m.alt = props.mediaCalcFourAlt;
          m.positionX = props.backgroundCalcFourPositionX;
          m.positionY = props.backgroundCalcFourPositionY;
        } else if ([null, 0, 1].indexOf(currentSelectedUsage.value) >= 0 && props.mediaCalcOneId && props.mediaCalcOneId !== '0') {
          m.id = props.mediaCalcOneId;
          m.sizes = props.mediaCalcOneSizes;
          m.alt = props.mediaCalcOneAlt;
          m.positionX = props.backgroundCalcOnePositionX;
          m.positionY = props.backgroundCalcOnePositionY;
        }
      }

      return m;
    });

    const subline = context.slots.subline ? context.slots.subline() : null;
    const hasSubline = computed(() => (
      subline && subline[0] ? `${subline[0].children}`.trim().length > 0 : false
    ));
    const content = context.slots.content ? context.slots.content() : null;
    const hasContent = computed(() => (
      content && content[0] ? `${content[0].children}`.trim().length > 0 : false
    ));

    const minHeight = ref<number | null>(null);

    const setMinHeight = () => {
      minHeight.value = null;
      let headerHeight = 0;

      if (header.$el) {
        headerHeight = header.$el.offsetHeight;
      }

      if (!isMobile.value) {
        switch (props.height) {
          case 'full':
            minHeight.value = window.innerHeight - headerHeight;
            break;
          case 'small':
            minHeight.value = (window.innerHeight - headerHeight) * 0.59537;
            break;
          case 'auto':
            minHeight.value = 0;
            break;
          default:
            minHeight.value = (window.innerHeight - headerHeight) * 0.68796;
        }
      }
    };

    const setIsMobile = () => {
      if (background.value && background.value.$el) {
        const {
          position,
        } = getComputedStyle(background.value.$el);

        isMobile.value = position !== 'absolute';
      } else {
        isMobile.value = false;
      }
    };

    const resizeHandler = () => {
      setMinHeight();
      setIsMobile();
    };

    const heroClasses = [
      'io-hero',
      `text-${props.fontColor}`,
    ];

    onMounted(() => {
      header.$el = document.querySelector('header');
      window.addEventListener('resize', resizeHandler);
      window.addEventListener('load', resizeHandler);
      resizeHandler();
      for (let i = 100; i < 1000; i += 100) {
        setTimeout(resizeHandler, i);
      }
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', resizeHandler);
      window.removeEventListener('load', resizeHandler);
    });

    watch(() => calculatorComponent.value, () => {
      nextTick(resizeHandler);
    });

    const onCalculatorChangeIcon = (icon: number) => {
      currentSelectedUsage.value = icon;
    };

    return {
      header,
      heroClasses,
      heroContainer,
      minHeight,
      hasSubline,
      hasContent,
      root,
      heroCalculator,
      onCalculatorChangeIcon,
      calculatorComponent,
      background,
      isMobile,
      currentSelectedUsage,
      currentMedia,
    };
  },
});
